import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import { useDeleteQuizSet } from '@/api/gen/quiz/quiz.gen';
import { showShareModal } from '@/components';
import { useOpenWindow } from '@/hooks';
import { useCreateAssignmentDialog } from '@/shared/googleClassroom';
import { usePlayUrl } from '@/shared/hooks';
import { useLtiSelectAction } from '@/shared/lti/hooks/useLtiSelectAction';
import { type QuizCardMenuItem } from '@/shared/quizList/hooks/useGetQuizListItemButtons';
import { useQuizCardEventTrack } from '@/shared/quizList/hooks/useQuizCardEventTrack';
import { confirmDialog } from '@/ui_v3';

export const useQuizListItemAction = (quizSet: SearchQuizsetRes) => {
  const router = useRouter();
  const { mutateAsync } = useDeleteQuizSet();
  const { t } = useTranslation(['common']);
  const { trackButtons } = useQuizCardEventTrack();
  const createAssignmentDialog = useCreateAssignmentDialog();
  const playUrl = usePlayUrl(quizSet.entryMapHashId);
  const { selectQuiz: selectLtiQuiz } = useLtiSelectAction();

  const openPage = useOpenWindow();

  const handleShareModal = () => {
    return showShareModal({
      ...quizSet,
      shareUrl: playUrl,
      contentType: 'QUIZSET',
      contentId: quizSet.id,
      contentOwnerId: quizSet.authorId,
      spaceHashId: quizSet.spaceHashId,
      trackLocation: 'public',
    });
  };

  const openLmsPage = () => {
    openPage(`/lms/${quizSet.id}/learning-report`);
  };
  const deleteAction = () =>
    mutateAsync({
      data: {
        id: quizSet.id,
      },
    }).then(() => {
      router.reload();
    });

  const onClickEvent = (actionType: QuizCardMenuItem['onClickEventName']) => {
    trackButtons(actionType);
    switch (actionType) {
      case 'lti_select': {
        void selectLtiQuiz({
          quizSetId: quizSet.id,
          entryMapHashId: quizSet.entryMapHashId,
          quizTitle: quizSet.title,
        });
        break;
      }
      case 'google_assignment': {
        createAssignmentDialog.open({
          quizSet,
        });
        break;
      }
      case 'share':
        handleShareModal();
        break;
      case 'copy':
        void router.push(`/duplicate/${quizSet.id}`);
        break;
      case 'report':
        openLmsPage();
        break;
      case 'enter':
        void router.push(`/play/${quizSet.entryMapHashId}`);
        break;
      case 'update':
        void router.push(`/update/${quizSet.id}`);
        break;
      case 'delete':
        void confirmDialog
          .openAsync({
            header: {
              title: t('common:quizListItem.error.delete'),
            },
            confirmIntent: 'destructive',
            cancelIntent: 'neutral',
          })
          .then(result => {
            if (result) {
              void deleteAction();
            }
          });

        break;
    }
  };

  return {
    onClickEvent,
  };
};
